import {
  ClinicalExtractionResult,
  ClinicalInterpretation,
  ClinicalMeasurementExtraction,
  ExtractedDocument,
  ExtractedNCSFinding,
  ExtractionEvidence
} from '../types/extraction';

const NERVE_SYNONYMS: Array<{ canonical: string; pattern: RegExp; type: ExtractedNCSFinding['type'] }> = [
  { canonical: 'median', pattern: /\b(mediano|median)\b/i, type: 'mixed' },
  { canonical: 'ulnar', pattern: /\b(cubital|ulnar)\b/i, type: 'mixed' },
  { canonical: 'radial', pattern: /\b(radial)\b/i, type: 'mixed' },
  { canonical: 'peroneal', pattern: /\b(peroneo|peroneal|fibular)\b/i, type: 'motor' },
  { canonical: 'tibial', pattern: /\b(tibial)\b/i, type: 'motor' },
  { canonical: 'sural', pattern: /\b(sural)\b/i, type: 'sensory' },
  { canonical: 'superficial_peroneal', pattern: /\b(peroneo superficial|superficial peroneal)\b/i, type: 'sensory' }
];

const TYPE_SYNONYMS = [
  { type: 'sensory' as const, pattern: /\b(PANS|sensitivo|sensitiva|sensorial|SNAP)\b/i },
  { type: 'motor' as const, pattern: /\b(PAMC|motor|motora|CMAP)\b/i }
];

const SIDE_PATTERNS = [
  { side: 'left' as const, pattern: /\.(i|izq|left)\b|(?:^|\s)(i|izq)\b/i },
  { side: 'right' as const, pattern: /\.(d|der|right)\b|(?:^|\s)(d|der)\b/i },
  { side: 'left' as const, pattern: /\b(izquierd[ao]|izq\.?|left)\b/i },
  { side: 'right' as const, pattern: /\b(derech[ao]|der\.?|right)\b/i },
  { side: 'bilateral' as const, pattern: /\b(bilateral|ambos|ambas)\b/i }
];

const INTERPRETATION_PATTERNS: Array<{
  field: 'latency' | 'peakLatency' | 'amplitude' | 'velocity';
  pattern: RegExp;
  interpretation: ClinicalInterpretation;
  label: string;
}> = [
  { field: 'peakLatency', pattern: /\blatencia\s+pico\b[^.]{0,40}(prolongad[ao]s?|aumentad[ao]s?)/i, interpretation: 'prolonged', label: 'latencia pico prolongada' },
  { field: 'latency', pattern: /\blatencias?\b[^.]{0,40}(prolongad[ao]s?|aumentad[ao]s?)/i, interpretation: 'prolonged', label: 'latencia prolongada' },
  { field: 'amplitude', pattern: /\bamplitud(?:es)?\b[^.]{0,40}(disminuid[ao]s?|baj[ao]s?|reducid[ao]s?)/i, interpretation: 'decreased', label: 'amplitud disminuida' },
  { field: 'velocity', pattern: /\bvelocidad(?:es)?\b[^.]{0,40}(disminuid[ao]s?|lent[ao]s?|baj[ao]s?)/i, interpretation: 'slowed', label: 'velocidad disminuida' },
  { field: 'latency', pattern: /\blatencias?\b[^.]{0,40}(normal(?:es)?|conservad[ao]s?|preservad[ao]s?|dentro de parametros normales|dentro de parámetros normales)/i, interpretation: 'normal', label: 'latencia conservada' },
  { field: 'peakLatency', pattern: /\blatencia\s+pico\b[^.]{0,40}(normal(?:es)?|conservad[ao]s?|preservad[ao]s?|dentro de parametros normales|dentro de parámetros normales)/i, interpretation: 'normal', label: 'latencia pico conservada' },
  { field: 'amplitude', pattern: /\bamplitud(?:es)?\b[^.]{0,40}(normal(?:es)?|conservad[ao]s?|preservad[ao]s?|dentro de parametros normales|dentro de parámetros normales)/i, interpretation: 'normal', label: 'amplitud conservada' },
  { field: 'velocity', pattern: /\bvelocidad(?:es)?\b[^.]{0,40}(normal(?:es)?|conservad[ao]s?|preservad[ao]s?|dentro de parametros normales|dentro de parámetros normales)/i, interpretation: 'normal', label: 'velocidad conservada' }
];

const ABSENT_RESPONSE_PATTERN = /\b(sin respuesta|no se logr[oó] evocar|no se evoca|no evoca|no evocado|no evocada|no evocable|ausente|inexcitable|inobtenible|no obtenible|no se obtiene respuesta|no se obtuvo respuesta)\b/i;
const NORMAL_SENTENCE_PATTERN = /\b(dentro de (?:los )?par[aá]metros normales|normal(?:es)?|conservad[ao]s?|preservad[ao]s?|sin alteraciones)\b/i;

export class ClinicalNarrativeExtractor {
  static extract(document: ExtractedDocument | string): ClinicalExtractionResult {
    const text = typeof document === 'string' ? document : document.plainText;
    const sentences = this.splitSentences(text);
    const findings: ExtractedNCSFinding[] = [];
    const warnings: string[] = [];

    sentences.forEach((sentence, index) => {
      const nerveMatches = NERVE_SYNONYMS.filter(nerve => nerve.pattern.test(sentence));
      if (nerveMatches.length === 0) return;

      const side = this.extractSide(sentence);
      const type = this.extractType(sentence, nerveMatches[0].type);
      const hasAbsentResponse = ABSENT_RESPONSE_PATTERN.test(sentence);
      const measurementInterpretations = this.extractMeasurements(sentence, hasAbsentResponse);
      const isNormalSentence = NORMAL_SENTENCE_PATTERN.test(sentence) && measurementInterpretations.length === 0 && !hasAbsentResponse;

      for (const nerve of nerveMatches) {
        const qualitativeFindings = measurementInterpretations.map(item => item.label);
        if (hasAbsentResponse) qualitativeFindings.push('sin respuesta evocable');
        if (isNormalSentence) qualitativeFindings.push('dentro de parámetros normales');

        if (qualitativeFindings.length === 0) continue;

        const evidence: ExtractionEvidence = { text: sentence, section: `sentence_${index + 1}` };
        findings.push({
          nerve: nerve.canonical,
          side,
          type,
          ...this.toMeasurementFields(measurementInterpretations, sentence, hasAbsentResponse),
          absentResponse: hasAbsentResponse,
          status: hasAbsentResponse || measurementInterpretations.some(item => item.interpretation !== 'normal') ? 'abnormal' : 'normal',
          qualitativeFindings,
          evidence: [evidence],
          confidence: this.calculateConfidence(side, type, hasAbsentResponse, measurementInterpretations.length, isNormalSentence),
          requiresReview: side === 'unknown' || type === 'unknown' || !hasAbsentResponse && measurementInterpretations.length === 0,
          parser: 'narrative'
        });
      }
    });

    if (findings.length === 0) {
      warnings.push('No se detectaron hallazgos narrativos NCS con el diccionario clínico actual.');
    }

    return {
      ncsFindings: findings,
      warnings,
      confidence: findings.length > 0 ? findings.reduce((sum, item) => sum + item.confidence, 0) / findings.length : 0
    };
  }

  private static splitSentences(text: string): string[] {
    return text
      .replace(/\s+/g, ' ')
      .split(/(?<=[.;:])\s+|\n+/)
      .map(sentence => sentence.trim())
      .filter(sentence => sentence.length > 8);
  }

  private static extractSide(sentence: string): ExtractedNCSFinding['side'] {
    const match = SIDE_PATTERNS.find(item => item.pattern.test(sentence));
    return match?.side || 'unknown';
  }

  private static extractType(sentence: string, fallback: ExtractedNCSFinding['type']): ExtractedNCSFinding['type'] {
    const match = TYPE_SYNONYMS.find(item => item.pattern.test(sentence));
    return match?.type || fallback;
  }

  private static extractMeasurements(sentence: string, absentResponse: boolean) {
    const matches = INTERPRETATION_PATTERNS
      .filter(item => item.pattern.test(sentence))
      .map(item => ({
        field: item.field,
        interpretation: item.interpretation,
        label: item.label
      }));

    if (absentResponse) {
      matches.push({ field: 'amplitude' as const, interpretation: 'absent' as const, label: 'amplitud ausente' });
    }

    return matches;
  }

  private static toMeasurementFields(
    matches: Array<{ field: 'latency' | 'peakLatency' | 'amplitude' | 'velocity'; interpretation: ClinicalInterpretation }>,
    rawText: string,
    absentResponse: boolean
  ): Partial<Pick<ExtractedNCSFinding, 'latency' | 'peakLatency' | 'amplitude' | 'velocity'>> {
    const fields: Partial<Pick<ExtractedNCSFinding, 'latency' | 'peakLatency' | 'amplitude' | 'velocity'>> = {};

    for (const match of matches) {
      const measurement: ClinicalMeasurementExtraction = {
        interpretation: match.interpretation,
        rawText,
        confidence: absentResponse || match.interpretation !== 'not_reported' ? 0.8 : 0.5
      };
      fields[match.field] = measurement;
    }

    return fields;
  }

  private static calculateConfidence(
    side: ExtractedNCSFinding['side'],
    type: ExtractedNCSFinding['type'],
    absentResponse: boolean,
    measurementCount: number,
    normalSentence: boolean
  ): number {
    let confidence = 0.55;
    if (side !== 'unknown') confidence += 0.15;
    if (type !== 'unknown' && type !== 'mixed') confidence += 0.1;
    if (measurementCount > 0) confidence += 0.15;
    if (absentResponse) confidence += 0.1;
    if (normalSentence) confidence += 0.05;
    return Math.min(confidence, 0.95);
  }
}
